import { Button } from "@dropbox/dig-components/buttons";
import { TextInput } from "@dropbox/dig-components/text_fields";
import { Text } from "@dropbox/dig-components/typography";
import { copyToClipboard } from "@mirage/service-platform-actions";
import { showSnackbar } from "@mirage/shared/snackbar";
import { useState } from "react";
import styles from "./SearchPanel.module.css";

const FLAG_EXAMPLE =
  "Flags will be added to the query as #answers_experimental_flags={key:value}";

export interface MagicFlagsConfigProps {
  magicFlags: Record<string, string>;
  onFlagsChange: (flags: Record<string, string>) => void;
  query: string;
}

export const MagicFlagsConfig = ({
  magicFlags,
  onFlagsChange,
  query,
}: MagicFlagsConfigProps) => {
  const [magicFlagKey, setMagicFlagKey] = useState("");
  const [magicFlagValue, setMagicFlagValue] = useState("");
  const [pasteInput, setPasteInput] = useState("");
  const [parseError, setParseError] = useState("");

  const addMagicFlag = () => {
    if (!magicFlagKey.trim()) return;

    onFlagsChange({
      ...magicFlags,
      [magicFlagKey]: magicFlagValue,
    });
    setMagicFlagKey("");
    setMagicFlagValue("");
  };

  const removeFlag = (flagKey: string) => {
    const nextMagicFlags = { ...magicFlags };
    delete nextMagicFlags[flagKey];
    onFlagsChange(nextMagicFlags);
  };

  const parsePastedFlags = () => {
    if (!pasteInput.trim()) return;

    setParseError("");

    try {
      // Try to parse as JSON first
      let parsed: Record<string, string>;

      // If it looks like JSON, parse it directly
      if (
        pasteInput.trim().startsWith("{") &&
        pasteInput.trim().endsWith("}")
      ) {
        parsed = JSON.parse(pasteInput);
      } else {
        // Try to convert key:value format to JSON
        // Handle formats like "key:value, foo:bar" or "key:value,foo:bar"
        const cleanInput = pasteInput
          .replace(/([^,{}\s]+):/g, '"$1":') // Add quotes around keys
          .replace(/:([^,{}\s]+)/g, ':"$1"') // Add quotes around values
          .replace(/,\s*/g, ","); // Clean up spacing

        const jsonString = `{${cleanInput}}`;
        parsed = JSON.parse(jsonString);
      }

      // Validate that all values are strings
      const validFlags: Record<string, string> = {};
      for (const [key, value] of Object.entries(parsed)) {
        validFlags[key] = String(value);
      }

      onFlagsChange({
        ...magicFlags,
        ...validFlags,
      });

      setPasteInput("");
    } catch (err) {
      setParseError(
        "Invalid format. Try: {key:value, foo:bar} or key:value, foo:bar",
      );
    }
  };

  const exportFlags = async () => {
    if (Object.keys(magicFlags).length === 0) return;

    try {
      const flagsString = JSON.stringify(magicFlags);
      copyToClipboard(flagsString);
      showSnackbar({
        title: "Flags copied to clipboard",
      });
    } catch (err) {
      // silent err
    }
  };

  const magicFlagsString = JSON.stringify(magicFlags);
  const magicFlagsQuery = `${query} #answers_experimental_flags=${magicFlagsString}`;
  const hasMagicFlags = Object.keys(magicFlags).length > 0;

  return (
    <div className={styles.magicFlagsSection}>
      <div className={styles.flagsHeader}>
        <div className={styles.flagsHeaderContent}>
          <Text size="small" isBold>
            Magic Flags
          </Text>
          <Text size="xsmall" color="subtle">
            {FLAG_EXAMPLE}
          </Text>
        </div>
        {hasMagicFlags && (
          <Button variant="opacity" size="small" onClick={exportFlags}>
            Export Flags
          </Button>
        )}
      </div>

      {hasMagicFlags && (
        <Text size="xsmall" color="subtle">
          {magicFlagsQuery}
        </Text>
      )}

      {Object.keys(magicFlags).length > 0 && (
        <div className={styles.magicFlagsList}>
          {Object.entries(magicFlags).map(([key, value]) => (
            <div key={key} className={styles.magicFlagItem}>
              <div className={styles.magicFlagInfo}>
                <Text size="small">{key}</Text>
                <Text size="small" color="subtle">
                  {value}
                </Text>
              </div>
              <Button
                variant="primary"
                size="small"
                onClick={() => removeFlag(key)}
              >
                Remove
              </Button>
            </div>
          ))}
        </div>
      )}

      <div className={styles.addMagicFlag}>
        <Text size="small" isBold>
          Add Individual Flag
        </Text>
        <div className={styles.magicFlagInputs}>
          <TextInput
            placeholder="Key"
            value={magicFlagKey}
            onChange={(e) => setMagicFlagKey(e.target.value)}
            size="small"
          />
          <TextInput
            placeholder="Value"
            value={magicFlagValue}
            onChange={(e) => setMagicFlagValue(e.target.value)}
            size="small"
          />
          <Button
            variant="primary"
            size="small"
            onClick={addMagicFlag}
            disabled={!magicFlagKey.trim()}
          >
            Add
          </Button>
        </div>
      </div>

      <div className={styles.pasteSection}>
        <Text size="small" isBold>
          Paste Multiple Flags
        </Text>
        <Text size="xsmall" color="subtle">
          Paste JSON or key:value pairs: {`{key:value, foo:bar}`} or key:value,
          foo:bar
        </Text>
        <textarea
          className={styles.pasteInput}
          placeholder="Paste flags here..."
          value={pasteInput}
          onChange={(e) => setPasteInput(e.target.value)}
          rows={1}
        />
        {parseError && (
          <Text size="xsmall" color="subtle" style={{ color: "#dc3545" }}>
            {parseError}
          </Text>
        )}
        <Button
          variant="primary"
          size="small"
          onClick={parsePastedFlags}
          disabled={!pasteInput.trim()}
        >
          Parse & Add
        </Button>
      </div>
    </div>
  );
};
